import { motion } from "framer-motion";
import { Handshake, ArrowRight, Wallet, Rocket, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Editable } from "@/components/builder/Editable";
import { useContent } from "@/context/ContentContext";

const STEPS = [
  { key: "s1", title: "Entra nel programma", desc: "Attivi il tuo profilo Partner in pochi minuti, direttamente dalla piattaforma." },
  { key: "s2", title: "Porta l'AI nei tuoi condomini", desc: "Ogni condominio che gestisci con Sedio contribuisce al tuo percorso da Partner." },
  { key: "s3", title: "Accumula crediti", desc: "Maturi crediti per ogni integrazione." },
];

const VANTAGGI = [
  { icon: Wallet, key: "b1", title: "Crediti flessibili", desc: "Li usi su nuove funzioni AI, moduli aggiuntivi o li puoi riscattare." },
  { icon: Rocket, key: "b2", title: "Studio più digitale", desc: "Offri ai condomini un servizio moderno, veloce e sempre reattivo." },
  { icon: TrendingUp, key: "b3", title: "Crescita condivisa", desc: "Cresci insieme a Sedio, con strumenti sempre all'avanguardia." },
];

export const Cashback = () => {
  const { ctaClick } = useContent();
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        <div className="section-blue relative overflow-hidden rounded-[2rem] px-6 py-12 shadow-xl ring-1 ring-white/10 sm:px-10 lg:px-12">
          <div className="absolute inset-0 bg-grid-faint opacity-[0.10]" aria-hidden="true" />
          <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" aria-hidden="true" />

          <div className="relative">
            {/* Intestazione */}
            <div className="max-w-2xl">
              <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-cyan-200 backdrop-blur">
                <Handshake className="h-4 w-4" /> <Editable path="cash.eyebrow">Programma Partner Sedio</Editable>
              </p>
              <Editable as="h2" path="cash.title" className="block font-display text-3xl font-extrabold text-white sm:text-4xl">
                Diventa Partner Sedio e matura crediti per far crescere il tuo studio.
              </Editable>
              <Editable as="p" path="cash.desc" className="mt-4 block text-base leading-7 text-white/90">
                Sedio Partner è pensato per gli amministratori che vogliono innovare sul serio. Ti affianchiamo nel portare l'intelligenza artificiale nella gestione quotidiana del condominio: un'opportunità concreta di crescita per il tuo studio e un servizio migliore per i tuoi condomini.
              </Editable>
            </div>

            <div className="mt-10 grid gap-5 lg:grid-cols-5">
              {/* Come funziona - percorso a step */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45 }}
                className="rounded-2xl border border-white/15 bg-white/[0.07] p-7 backdrop-blur-sm lg:col-span-3"
              >
                <h3 className="font-display text-xl font-bold text-white"><Editable path="cash.stepsTitle">Come funziona</Editable></h3>
                <ol className="relative mt-6 space-y-7 before:absolute before:left-[19px] before:top-3 before:h-[calc(100%-2.5rem)] before:w-px before:bg-white/20">
                  {STEPS.map((s, i) => (
                    <li key={s.key} className="relative flex gap-4">
                      <span className="relative z-10 inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-cyan-300 font-display text-base font-extrabold text-[hsl(226_47%_10%)] shadow-sm">
                        {i + 1}
                      </span>
                      <div className="pt-1">
                        <h4 className="font-semibold text-white"><Editable path={`cash.${s.key}.title`}>{s.title}</Editable></h4>
                        <p className="mt-1 text-sm leading-6 text-white/80"><Editable path={`cash.${s.key}.desc`}>{s.desc}</Editable></p>
                      </div>
                    </li>
                  ))}
                </ol>
              </motion.div>

              {/* I vantaggi */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: 0.1 }}
                className="flex flex-col rounded-2xl border border-white/15 bg-white/[0.07] p-7 backdrop-blur-sm lg:col-span-2"
              >
                <h3 className="font-display text-xl font-bold text-white"><Editable path="cash.vantaggiTitle">Cosa ottieni</Editable></h3>
                <ul className="mt-5 space-y-5">
                  {VANTAGGI.map((v) => (
                    <li key={v.key} className="flex items-start gap-3">
                      <span className="mt-0.5 inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-white/10 text-cyan-200 ring-1 ring-white/10"><v.icon className="h-4 w-4" strokeWidth={2} /></span>
                      <div>
                        <h4 className="text-sm font-semibold text-white"><Editable path={`cash.${v.key}.title`}>{v.title}</Editable></h4>
                        <p className="mt-0.5 text-sm leading-6 text-white/80"><Editable path={`cash.${v.key}.desc`}>{v.desc}</Editable></p>
                      </div>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>

            <div className="mt-10 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Button data-testid="cashback-cta-button" size="lg" onClick={ctaClick("cash.cta.link", "https://app.sedio.it")} className="btn-cta h-12 gap-2 px-7 text-base">
                <Editable path="cash.cta">Provalo gratis</Editable> <ArrowRight className="h-4 w-4" />
              </Button>
              <p className="text-sm text-white/80"><Editable path="cash.ctaNote">Accedi gratis e scopri le condizioni del programma, senza impegno.</Editable></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
